import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Search, User, Phone, Store, ShoppingBag, Package, Coins, TrendingUp, Receipt } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RANGES = [
  { key: "day", label: "اليوم" },
  { key: "month", label: "الشهر" },
  { key: "year", label: "السنة" },
  { key: "all", label: "كل الوقت" },
];

/**
 * Admin tab "تفاصيل الزبون":
 * - Search bar → debounced suggestions (name / market / phone).
 * - Pick a customer → 4 stat cards (invoices, cartons, revenue, rewards
 *   earned) with day/month/year/all filters. Reads from raw `orders` so
 *   it survives any monthly contest reset on the customer's home page.
 */
const CustomerDetailsTab = () => {
  const token = localStorage.getItem("jumlaty_token");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [range, setRange] = useState("month");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/admin/customer-details/search`, {
          ...auth, params: { q: query.trim() },
        });
        setSuggestions(Array.isArray(data) ? data : []);
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const fetchStats = async (userId, r) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/customer-details/${userId}`, {
        ...auth, params: { range: r },
      });
      setStats(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذر تحميل التفاصيل");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selected) fetchStats(selected.id, range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, selected?.id]);

  const fmt = (n) => Number(n || 0).toLocaleString("en-US");

  return (
    <div className="space-y-6">
      {/* ── Search bar ─────────────────────────────────────────────── */}
      <div className="bg-white rounded-3xl shadow-md border border-[#E4EBE6] p-5">
        <h3 className="text-lg font-extrabold text-[#111814] mb-3 flex items-center gap-2">
          <User className="w-5 h-5 text-[#2D4A3E]" />
          تفاصيل الزبون
        </h3>
        <div className="relative">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="cd-search"
            placeholder="ابحث بالاسم، الماركت، أو رقم الهاتف..."
            className="w-full bg-[#F4F1E8] border border-[#E8D9B5] rounded-2xl pr-10 pl-4 py-3 text-sm focus:outline-none focus:border-[#15803D]"
          />
          <Search className="w-4 h-4 text-[#5C6E64] absolute right-3 top-1/2 -translate-y-1/2" />

          <AnimatePresence>
            {suggestions.length > 0 && !selected && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="absolute z-30 left-0 right-0 mt-1 bg-white border border-[#E4EBE6] rounded-2xl shadow-xl overflow-hidden max-h-72 overflow-y-auto"
                data-testid="cd-suggestions"
              >
                {suggestions.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => { setSelected(u); setSuggestions([]); setQuery(u.full_name); }}
                    data-testid={`cd-suggest-${u.id}`}
                    className="w-full text-right px-4 py-2.5 hover:bg-[#F4F1E8] border-b border-[#E4EBE6] last:border-b-0 transition-colors"
                  >
                    <div className="font-bold text-[#111814]">{u.full_name}</div>
                    <div className="text-xs text-[#5C6E64] flex items-center gap-3 mt-0.5">
                      {u.market_name && <span><Store className="inline w-3 h-3 ml-1" />{u.market_name}</span>}
                      <span dir="ltr"><Phone className="inline w-3 h-3 ml-1" />{u.phone}</span>
                    </div>
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* ── Stats panel ────────────────────────────────────────────── */}
      {selected && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-md border border-[#E4EBE6] p-5"
        >
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <div className="text-xs text-[#5C6E64]">الزبون المختار</div>
              <div className="text-xl font-extrabold text-[#111814]">{selected.full_name}</div>
              {stats?.user?.market_name && (
                <div className="text-sm text-[#15803D] font-bold mt-0.5">
                  <Store className="inline w-3.5 h-3.5 ml-1" />{stats.user.market_name}
                </div>
              )}
              <div className="text-xs text-[#5C6E64] mt-1" dir="ltr">{selected.phone}</div>
            </div>
            <button
              onClick={() => { setSelected(null); setStats(null); setQuery(""); }}
              data-testid="cd-clear"
              className="text-xs text-[#E85D45] hover:text-[#c14a36] font-bold"
            >
              إلغاء التحديد ✕
            </button>
          </div>

          {/* Range pills */}
          <div className="flex flex-wrap gap-2 mb-4">
            {RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => setRange(r.key)}
                data-testid={`cd-range-${r.key}`}
                className={`text-xs font-bold px-4 py-1.5 rounded-full transition-colors border ${
                  range === r.key
                    ? "bg-[#15803D] text-white border-[#15803D]"
                    : "bg-white text-[#15803D] border-[#15803D]/40 hover:bg-[#DCFCE7]"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-8 text-[#5C6E64]">جاري التحميل...</div>
          ) : stats ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="cd-stats">
              <StatCard
                icon={<Receipt className="w-5 h-5" />}
                label="عدد الفواتير"
                value={fmt(stats.invoices_count)}
                unit="فاتورة"
                color="from-[#3B82F6] to-[#1E3A5F]"
                testId="cd-stat-invoices"
              />
              <StatCard
                icon={<Package className="w-5 h-5" />}
                label="عدد الكراتين"
                value={fmt(stats.cartons_count)}
                unit="كرتونة"
                color="from-[#15803D] to-[#126B32]"
                testId="cd-stat-cartons"
              />
              <StatCard
                icon={<TrendingUp className="w-5 h-5" />}
                label="إجمالي الأرباح"
                value={fmt(Math.round(stats.total_revenue))}
                unit="د.ع"
                color="from-[#F5A623] to-[#d18707]"
                testId="cd-stat-revenue"
              />
              <StatCard
                icon={<Coins className="w-5 h-5" />}
                label="مكافآت ١٥٠ د.ع/قطعة"
                value={fmt(stats.rewards_earned_total)}
                unit="د.ع"
                color="from-[#7e22ce] to-[#5b21b6]"
                testId="cd-stat-rewards"
              />
            </div>
          ) : null}

          {/* Live current balances row */}
          {stats?.user && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-[#FFF4E0] border border-[#F5A623]/30 rounded-2xl p-3 flex items-center gap-3">
                <ShoppingBag className="w-5 h-5 text-[#F5A623]" />
                <div>
                  <div className="text-[11px] text-[#8B7E5A]">رصيد المكافآت الحالي</div>
                  <div className="font-extrabold text-[#F5A623]" dir="ltr">
                    {fmt(stats.user.rewards_balance_current)} د.ع
                  </div>
                </div>
              </div>
              <div className="bg-[#FEE2E2] border border-[#E85D45]/30 rounded-2xl p-3 flex items-center gap-3">
                <Receipt className="w-5 h-5 text-[#E85D45]" />
                <div>
                  <div className="text-[11px] text-[#8B7E5A]">الديون المتراكمة (سجل دائم)</div>
                  <div className="font-extrabold text-[#E85D45]" dir="ltr">
                    {fmt(stats.user.customer_debt)} د.ع
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="text-[11px] text-[#5C6E64] mt-4 leading-relaxed bg-[#F0F9FF] border border-[#BAE6FD] rounded-xl p-3">
            ℹ️ هذه التفاصيل دائمية ومحفوظة حتى لو تم تصفير المسابقة الشهرية بواجهة الزبون.
            عدد الكراتين والمكافآت (١٥٠ د.ع/قطعة) محسوبة من سجل الفواتير الأصلي ولا تتأثر بأي تصفير.
          </div>
        </motion.div>
      )}
    </div>
  );
};

const StatCard = ({ icon, label, value, unit, color, testId }) => (
  <div
    data-testid={testId}
    className={`bg-gradient-to-br ${color} text-white rounded-2xl p-4 shadow-md`}
  >
    <div className="flex items-center justify-between mb-2 opacity-90">
      {icon}
      <span className="text-[10px] font-bold uppercase tracking-wider opacity-70">{unit}</span>
    </div>
    <div className="text-[11px] opacity-85 font-medium">{label}</div>
    <div className="text-2xl font-extrabold tabular-nums leading-tight mt-0.5">{value}</div>
  </div>
);

export default CustomerDetailsTab;
